import type {ScheduleSlot} from '~/types/schedule/slot';
import type {Veterinarian, VeterinarianBookingNearestSlot} from '~/types/veterinar';

type SlotDoctor = Pick<Veterinarian, 'availableSlots' | 'booking' | 'price'>;

type ClinicPageDoctor = SlotDoctor & {
  specialties?: {price?: number | null}[];
  receptionType?: string | null;
};

function parseSlotTs(dateDmY: string, timeHm: string, startTime?: number): number {
  const seg = dateDmY.split('.').map(Number);
  if (seg.length !== 3 || seg.some((n) => Number.isNaN(n))) {
    return Number.POSITIVE_INFINITY;
  }
  const [day, month, year] = seg;
  if (typeof startTime === 'number' && Number.isFinite(startTime)) {
    return new Date(year, month - 1, day, Math.floor(startTime / 60), startTime % 60).getTime();
  }
  const [hours, minutes] = timeHm.split(':').map(Number);
  return new Date(year, month - 1, day, hours || 0, minutes || 0).getTime();
}

function slotFromBooking(vet: SlotDoctor): VeterinarianBookingNearestSlot | null {
  const booked = vet.booking?.nearestSlot;
  if (!booked?.date?.trim() || !booked?.time?.trim()) {
    return null;
  }
  return booked;
}

function slotFromAvailable(vet: SlotDoctor): VeterinarianBookingNearestSlot | null {
  const days = vet.availableSlots;
  if (!days) {
    return null;
  }

  let best: {date: string; time: string; ts: number} | null = null;
  for (const [date, daySlots] of Object.entries(days)) {
    for (const slot of (daySlots ?? []) as ScheduleSlot[]) {
      if (slot.busy) {
        continue;
      }
      const time = slot.startTimeFormatted?.trim();
      if (!time) {
        continue;
      }
      const ts = parseSlotTs(date, time, slot.startTime);
      if (!best || ts < best.ts) {
        best = {date, time, ts};
      }
    }
  }

  if (!best) {
    return null;
  }

  return {
    date: best.date,
    time: best.time,
    price: vet.price ?? 0,
  };
}

/** Ближайший свободный слот врача: из booking или из availableSlots листинга. */
export function pickNearestDoctorSlot(vet: SlotDoctor): VeterinarianBookingNearestSlot | null {
  return slotFromBooking(vet) ?? slotFromAvailable(vet);
}

export function compareDoctorsByNearestSlot(left: SlotDoctor, right: SlotDoctor): number {
  const leftSlot = pickNearestDoctorSlot(left);
  const rightSlot = pickNearestDoctorSlot(right);
  if (leftSlot && !rightSlot) {
    return -1;
  }
  if (!leftSlot && rightSlot) {
    return 1;
  }
  if (!leftSlot || !rightSlot) {
    return 0;
  }
  return parseSlotTs(leftSlot.date, leftSlot.time) - parseSlotTs(rightSlot.date, rightSlot.time);
}

/** Карточка на странице клиники показывает слоты только при цене и типе приёма. */
export function clinicPageDoctorShowsSlots(vet: ClinicPageDoctor): boolean {
  const hasPrice = (vet.price ?? 0) > 0
    || (vet.specialties ?? []).some((specialty) => (specialty.price ?? 0) > 0);
  if (!hasPrice || !vet.receptionType) {
    return false;
  }

  return pickNearestDoctorSlot(vet) !== null;
}

/** Сначала врачи, у которых на карточке видны слоты, затем по ближайшему времени. */
export function compareClinicPageDoctors(left: ClinicPageDoctor, right: ClinicPageDoctor): number {
  const leftShows = clinicPageDoctorShowsSlots(left);
  const rightShows = clinicPageDoctorShowsSlots(right);
  if (leftShows !== rightShows) {
    return leftShows ? -1 : 1;
  }
  if (!leftShows) {
    return 0;
  }

  return compareDoctorsByNearestSlot(left, right);
}
